// Failing-first stub (task 4.1): the tilt-projection pure seam (design D10, UI.md §8).
// World coordinates project to screen with the world y-axis squashed ×0.72 and no x
// shear; picking (screenToWorld) inverts the exact same transform. Sprite depth scale
// is a separate pure function of the *projected* screen y (`0.85 + 0.4·(y/h)`), since
// scale is a rendering-time concern of screen position, not of the world→screen
// transform itself.

/** World-space y is squashed by this factor when projected to screen (UI.md §8). */
export const Y_SQUASH = 0.72;

/** A simple uniform pan/zoom camera: `offset` is the world point mapped to screen
 * origin `[0, 0]`; `scale` is screen-units-per-world-unit (applied before the squash). */
export interface Camera {
  offsetX: number;
  offsetY: number;
  scale: number;
}

export interface ScreenPoint {
  x: number;
  y: number;
}

/** The shared wide establishing shot both in-match routes default to: the whole
 * seed-size field in non-negative screen space at scale 0.2 (819×590 on screen).
 * World space is CENTRED (the lod.rs quantization contract maps ±fieldSize/2 to
 * the u16 range), and `offset` is the world point mapped to screen ORIGIN — not a
 * "look-at" centre — so the establishing offset is the field's top-left corner
 * (-2048, -2048) at the 4096 seed field size. (The old per-route defaults set
 * offset to (2048, 2048) as if it meant look-at in a 0-based space, projecting
 * the entire field off-canvas — the blank-field bug.) Real pan/zoom camera
 * control is the deferred gesture work (D-071 follow-ups). */
export const ESTABLISHING_CAMERA: Camera = { offsetX: -2048, offsetY: -2048, scale: 0.2 };

/** The close-tier display scale for the colony play view's hive-centred field underlay
 * (design DD9, `colony-field-canvas`): a legible **display constant** — a fixed
 * establishing colony shot showing the hive ring + immediate forage neighbourhood — **not**
 * a balance tunable (no sim/balance behaviour changes) and needing no sweep. Real pan/zoom
 * gesture control of the camera stays deferred (D-071 follow-ups). */
export const COLONY_CLOSE_SCALE = 1.0;

/** The nominal Stage-3 pixel viewport the colony establishing shot is framed for (design
 * DD9, `colony-field-canvas`): a fixed establishing colony shot, so the hive camera and the
 * `Subscribe` hive viewport both derive from this one size — real on-device resize/pan/zoom
 * gesture control (and a resize observer) is the deferred D-071 camera follow-up. */
export const COLONY_NOMINAL_VIEWPORT = { width: 402, height: 874 } as const;

/** The hive-centred close-tier camera for the colony play view (design DD7, `client-
 * rendering`): a **pure** helper returning a `Camera` at [`COLONY_CLOSE_SCALE`] whose
 * `offset` places `ownHive` at the centre of a viewport of the given pixel size — the exact
 * inverse of {@link worldToScreen} at screen centre (`offsetX = hx − (W/2)/scale`, `offsetY =
 * hy − (H/2)/(scale·Y_SQUASH)`), so the own hive renders centred. Total: a `null` `ownHive`
 * returns {@link ESTABLISHING_CAMERA} rather than a NaN camera (a belt-and-braces path — the
 * colony underlay is gated on `ownHive` present, so the live path never hits it). */
export function hiveCloseCamera(
  ownHive: readonly [number, number] | null,
  viewport: { width: number; height: number },
): Camera {
  if (ownHive === null) {
    return ESTABLISHING_CAMERA;
  }
  const scale = COLONY_CLOSE_SCALE;
  return {
    offsetX: ownHive[0] - viewport.width / 2 / scale,
    offsetY: ownHive[1] - viewport.height / 2 / (scale * Y_SQUASH),
    scale,
  };
}

export function worldToScreen(camera: Camera, world: readonly [number, number]): ScreenPoint {
  const [wx, wy] = world;
  return {
    x: (wx - camera.offsetX) * camera.scale,
    y: (wy - camera.offsetY) * camera.scale * Y_SQUASH,
  };
}

export function screenToWorld(camera: Camera, screen: ScreenPoint): [number, number] {
  return [
    screen.x / camera.scale + camera.offsetX,
    screen.y / (camera.scale * Y_SQUASH) + camera.offsetY,
  ];
}

/** Sprite scale for a projected screen y within a viewport of `viewportHeight`
 * (UI.md §8: `0.85 + 0.4·(y/h)`, ×0.85 at the top edge growing to ×1.25 at the bottom). */
export function depthScale(screenY: number, viewportHeight: number): number {
  return 0.85 + 0.4 * (screenY / viewportHeight);
}

// ---------------------------------------------------------------------------
// Sprite scale: sprites are anchored to the WORLD, not to the screen
// ---------------------------------------------------------------------------
//
// Sprites used to be drawn at a fixed screen size, carrying only the `depthScale`
// depth cue — so a bee stayed 24 px whether you were surveying the whole field or
// leaning into one hive, while the primitive-drawn hive cells and patch meters DID
// scale with the camera. That split is what made a zoom sweep read wrong: the ground
// moved and the things standing on it did not, and the hive comb grew out from under
// bees that stayed the same size.
//
// Every sprite now takes its size from the camera, like every primitive already did.
// The calibration is expressed as the zoom at which a sprite renders at its native art
// size, so the art itself needs no re-authoring.

/** The camera zoom at which a sprite renders at its native art size. Below it sprites
 * shrink with the world, above it they grow. Set at 2× so the shipped art reads at its
 * intended size when a player has leaned in on a hive, which is the zoom the close-tier
 * detail is actually for. */
export const SPRITE_NATIVE_ZOOM = 2;

/** The ground scatter's world-size multiplier over the sprite baseline. Grass tufts and
 * rocks are wayfinding landmarks rather than entities — they read as texture, so they carry
 * a larger world footprint than a bee for the same art. Halved from a first pass at 4.5,
 * which made the scatter compete with the colony for attention instead of dressing the
 * ground under it. */
export const SCATTER_SIZE_MULTIPLIER = 2.25;

/** The floor on a sprite's scale. A far zoom-out should shrink sprites, not collapse them
 * to a zero-size node — and a degenerate camera (0, negative, non-finite) must not put NaN
 * geometry into the scene graph. */
export const MIN_SPRITE_SCALE = 0.05;

/**
 * A sprite's camera-driven scale: `cameraScale / SPRITE_NATIVE_ZOOM`, times an optional
 * per-family `multiplier`. Multiply by {@link depthScale} for the final node scale — the
 * two are orthogonal (one is "how far in is the camera", the other "how near the front of
 * the tilt plane is this thing"). Pure and total.
 */
export function spriteCameraScale(cameraScale: number, multiplier = 1): number {
  if (!Number.isFinite(cameraScale) || cameraScale <= 0) {
    return MIN_SPRITE_SCALE;
  }
  return Math.max(MIN_SPRITE_SCALE, (cameraScale / SPRITE_NATIVE_ZOOM) * multiplier);
}
